const DEFAULT_ZONE_OPTIONS = [
  { id: 'inicio', label: 'Inicio', shortLabel: 'Inicio' },
  { id: 'construccion', label: 'Construcción', shortLabel: 'Construcción' },
  { id: 'finalizacion', label: 'Finalización', shortLabel: 'Finalización' },
];

const DEFAULT_LANE_OPTIONS = [
  { id: 'izquierdo', label: 'Izquierdo', shortLabel: 'Izquierdo' },
  { id: 'central', label: 'Central', shortLabel: 'Central' },
  { id: 'derecho', label: 'Derecho', shortLabel: 'Derecho' },
];

function slugify(value, fallback = 'item') {
  const slug = String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

  return slug || fallback;
}

function getNamedValue(source, keys = []) {
  for (const key of keys) {
    const value = source?.[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return undefined;
}

function normalizeActionType(value) {
  const normalized = String(value || 'instant').toLowerCase();
  return normalized.includes('interval') ? 'interval' : 'instant';
}

function getDefaultOptions(type) {
  if (type === 'zone') return DEFAULT_ZONE_OPTIONS;
  if (type === 'lane') return DEFAULT_LANE_OPTIONS;
  return [];
}

function normalizeSelectorOption(option, fallback = {}) {
  const label = option?.label || fallback.label || option?.id || fallback.id || 'Opción';
  const shortLabel = option?.shortLabel || option?.abbr || label.slice(0, 1).toUpperCase();

  return {
    id: option?.id || fallback.id || slugify(label, 'option'),
    label,
    shortLabel,
    color: option?.color,
    hotkey: option?.hotkey || option?.shortcut,
  };
}

function resolveLegacySelectorOptions(panel, type) {
  const sourceOptions = type === 'zone'
    ? panel?.zonas || panel?.zones || panel?.zoneOptions
    : panel?.carriles || panel?.pasillos || panel?.lanes || panel?.laneOptions;

  if (!Array.isArray(sourceOptions) || sourceOptions.length === 0) {
    return getDefaultOptions(type);
  }

  return sourceOptions.map((option, index) => {
    if (typeof option === 'string') {
      return normalizeSelectorOption(
        { id: slugify(option, `${type}_${index}`), label: option },
        getDefaultOptions(type)[index]
      );
    }

    return normalizeSelectorOption(
      {
        ...option,
        id: option?.id || option?.value,
        label: getNamedValue(option, ['label', 'nombre', 'name']) || option?.id || option?.value,
        shortLabel: option?.shortLabel || option?.abbr,
      },
      getDefaultOptions(type)[index]
    );
  });
}

function buildLegacyGroups(panel) {
  if (Array.isArray(panel?.groups) && panel.groups.length > 0) {
    return panel.groups;
  }

  const sourceButtons = Array.isArray(panel?.botones)
    ? panel.botones
    : Array.isArray(panel?.buttons)
      ? panel.buttons
      : [];

  if (!sourceButtons.length) return [];

  const groups = [];
  const groupMap = new Map();

  sourceButtons.forEach((button, index) => {
    const groupId = getNamedValue(button, ['group', 'grupo', 'groupId', 'categoria', 'category']) || 'general';
    const groupLabel = getNamedValue(button, ['groupLabel', 'grupoNombre', 'categoryLabel']) || groupId;
    const groupColor = button?.groupColor || button?.colorGrupo;

    if (!groupMap.has(groupId)) {
      const group = {
        id: groupId,
        name: groupLabel,
        label: groupLabel,
        color: groupColor,
        order: groups.length,
        actions: [],
      };
      groupMap.set(groupId, group);
      groups.push(group);
    }

    groupMap.get(groupId).actions.push({
      ...button,
      id: button?.id || slugify(getNamedValue(button, ['label', 'nombre', 'name']) || `action_${index}`, `action_${index}`),
      label: getNamedValue(button, ['label', 'nombre', 'name']) || `Acción ${index + 1}`,
      type: normalizeActionType(button?.type || button?.tipo),
      hotkey: button?.hotkey || button?.shortcut,
      order: button?.order ?? groupMap.get(groupId).actions.length,
    });
  });

  return groups;
}

function getLegacySpatialContext(panel) {
  const sc = panel?.spatialContext || {};
  const groups = Array.isArray(panel?.groups) ? panel.groups : [];
  const zoneSelector = Boolean(sc.zoneSelector ?? panel?.zonaCarrilEnabled
    ?? groups.some(group => group.spatialContext?.zoneSelector));
  const laneSelector = Boolean(sc.laneSelector ?? panel?.zonaCarrilEnabled
    ?? groups.some(group => group.spatialContext?.laneSelector));
  return {
    zoneSelector,
    laneSelector,
    showTeams: Boolean(sc.showTeams ?? panel?.showTeams ?? true),
    showPlayers: Boolean(sc.showPlayers ?? panel?.playerConfig?.enabled ?? panel?.showPlayers ?? true),
    showFieldZone: Boolean(sc.showFieldZone ?? sc.fieldZoneEnabled ?? panel?.showZone ?? (zoneSelector || laneSelector)),
  };
}

export function buildLegacyContextSelectors(panel) {
  const spatialContext = getLegacySpatialContext(panel);
  const selectors = [];

  if (spatialContext.zoneSelector) {
    selectors.push({
      id: 'zona',
      type: 'zone',
      label: 'Zona',
      descriptorKey: 'zona',
      behavior: 'sticky',
      options: resolveLegacySelectorOptions(panel, 'zone'),
      order: 0,
    });
  }

  if (spatialContext.laneSelector) {
    selectors.push({
      id: 'pasillo',
      type: 'lane',
      label: 'Carril',
      descriptorKey: 'pasillo',
      behavior: 'sticky',
      options: resolveLegacySelectorOptions(panel, 'lane'),
      order: selectors.length,
    });
  }

  return selectors;
}

export function normalizeContextSelectors(panel) {
  const sourceSelectors = Array.isArray(panel?.contextSelectors) && panel.contextSelectors.length > 0
    ? panel.contextSelectors
    : buildLegacyContextSelectors(panel);

  return sourceSelectors.map((selector, index) => {
    const defaultOptions = getDefaultOptions(selector?.type);
    const sourceOptions = Array.isArray(selector?.options) && selector.options.length > 0
      ? selector.options
      : defaultOptions;

    return {
      ...selector,
      id: selector?.id || `${selector?.type || 'context'}_${index}`,
      label: selector?.label || (selector?.type === 'zone' ? 'Zona' : selector?.type === 'lane' ? 'Carril' : 'Contexto'),
      descriptorKey: selector?.descriptorKey || selector?.id || `${selector?.type || 'context'}_${index}`,
      behavior: selector?.behavior || 'sticky',
      options: sourceOptions.map((option, optionIndex) => normalizeSelectorOption(option, defaultOptions[optionIndex])),
      order: selector?.order ?? index,
    };
  });
}

function normalizeDescriptorButtons(descriptorButtons = []) {
  return descriptorButtons
    .map((descriptor, index) => ({
      ...descriptor,
      id: descriptor?.id || slugify(getNamedValue(descriptor, ['label', 'nombre', 'name']) || `descriptor_${index}`, `descriptor_${index}`),
      label: getNamedValue(descriptor, ['label', 'nombre', 'name']) || descriptor?.id || `Descriptor ${index + 1}`,
      group: descriptor?.group || descriptor?.grupo,
      hotkey: descriptor?.hotkey || descriptor?.shortcut,
    }))
    .filter((descriptor) => descriptor.id && descriptor.label);
}

function normalizeGroups(groups = []) {
  return groups.map((group, groupIndex) => {
    const sourceActions = Array.isArray(group?.actions)
      ? group.actions
      : Array.isArray(group?.buttons)
        ? group.buttons
        : [];

    return {
      ...group,
      id: group?.id || slugify(getNamedValue(group, ['name', 'label', 'nombre']) || `group_${groupIndex}`, `group_${groupIndex}`),
      name: getNamedValue(group, ['name', 'label', 'nombre']) || group?.id || `Grupo ${groupIndex + 1}`,
      label: getNamedValue(group, ['label', 'name', 'nombre']) || group?.id || `Grupo ${groupIndex + 1}`,
      actions: sourceActions.map((action, actionIndex) => ({
        ...action,
        id: action?.id || slugify(getNamedValue(action, ['label', 'nombre', 'name']) || `action_${groupIndex}_${actionIndex}`, `action_${groupIndex}_${actionIndex}`),
        label: getNamedValue(action, ['label', 'nombre', 'name']) || action?.id || `Acción ${actionIndex + 1}`,
        type: normalizeActionType(action?.type || action?.tipo),
        hotkey: action?.hotkey || action?.shortcut,
        descriptorOptions: action?.descriptorOptions || action?.customDescriptors,
      })),
    };
  });
}

function buildLegacyButtons(groups = []) {
  return groups.flatMap((group) =>
    (group?.actions || []).map((action) => ({
      ...action,
      nombre: getNamedValue(action, ['label', 'nombre', 'name']) || action?.id || 'Acción',
      name: getNamedValue(action, ['label', 'nombre', 'name']) || action?.id || 'Acción',
      shortcut: action?.hotkey || action?.shortcut,
      grupo: group?.id,
      group: group?.id,
      grupoNombre: group?.name || group?.label,
      groupLabel: group?.label || group?.name,
    }))
  );
}

function buildLegacyDescriptors(descriptorButtons = []) {
  return descriptorButtons.map((descriptor) => ({
    ...descriptor,
    nombre: getNamedValue(descriptor, ['label', 'nombre', 'name']) || descriptor?.id || 'Descriptor',
    name: getNamedValue(descriptor, ['label', 'nombre', 'name']) || descriptor?.id || 'Descriptor',
    shortcut: descriptor?.hotkey || descriptor?.shortcut,
    grupo: descriptor?.group || descriptor?.grupo,
  }));
}

function buildLegacySelectorExport(source, contextSelectors, type) {
  const selectorOptions = (contextSelectors || []).find((selector) => selector?.type === type)?.options;
  const options = Array.isArray(selectorOptions) && selectorOptions.length > 0
    ? selectorOptions
    : resolveLegacySelectorOptions(source, type);

  return options.map((option) => ({
    ...option,
    nombre: option?.label || option?.nombre || option?.name || option?.id || 'Opción',
    name: option?.label || option?.nombre || option?.name || option?.id || 'Opción',
    abbr: option?.shortLabel || option?.abbr,
    shortcut: option?.hotkey || option?.shortcut,
  }));
}

export function normalizePanelDefinition(panel) {
  if (!panel) return null;

  if (panel.type === 'ButtonPanelPack' && panel.schema) {
    const schema = panel.schema || {};
    return normalizePanelDefinition({
      ...schema,
      id: panel.id || schema.id,
      name: panel.name || schema.name || schema.nombre,
      version: panel.version || schema.version || '1.0.0',
      sport: panel.sport || schema.sport || 'football',
      createdAt: panel.exportedAt || schema.createdAt || new Date().toISOString(),
      updatedAt: schema.updatedAt,
      source: panel.source || 'soccerAnalytics-desktop',
    });
  }

  const source = panel?.config && !Array.isArray(panel?.groups)
    ? {
        ...panel.config,
        id: panel.id || panel.config.id,
        name: panel.config?.name || panel.config?.nombre || panel.nombre || panel.name,
        version: panel.version || panel.config?.version,
        createdAt: panel.createdAt || panel.config?.createdAt,
        updatedAt: panel.updatedAt || panel.config?.updatedAt,
      }
    : panel;

  const legacyGroups = buildLegacyGroups(source);
  const groups = normalizeGroups(legacyGroups);
  const descriptorButtons = normalizeDescriptorButtons(
    source?.descriptorButtons || source?.descriptores || source?.descriptors || []
  );
  const contextSelectors = normalizeContextSelectors(source);
  const spatialContext = getLegacySpatialContext(source);

  return {
    ...source,
    id: source?.id,
    name: source?.name || source?.nombre || 'Panel',
    nombre: source?.nombre || source?.name || 'Panel',
    version: String(source?.version || '1.0'),
    createdAt: source?.createdAt || new Date().toISOString(),
    updatedAt: source?.updatedAt,
    groups,
    botones: buildLegacyButtons(groups),
    descriptorButtons,
    descriptores: buildLegacyDescriptors(descriptorButtons),
    ...(() => {
      const raw = source?.descriptorGroups;
      if (Array.isArray(raw)) {
        // New format: [{ id, name, color, order }]
        const sorted = [...raw].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        return {
          descriptorGroups: sorted,
          descriptorGroupOrder: sorted.map((g) => g.id),
        };
      }
      // Old format: descriptorGroupOrder string[] + optional descriptorGroups map
      const order = Array.isArray(source?.descriptorGroupOrder) ? source.descriptorGroupOrder : undefined;
      const savedMap = (raw && typeof raw === 'object') ? raw : {};
      const ids = order || [];
      const groups = ids
        .filter((id) => id !== 'ungrouped')
        .map((id, index) => ({
          id,
          name: savedMap[id]?.name || id,
          color: savedMap[id]?.color || '',
          order: index,
        }));
      return {
        descriptorGroups: groups.length ? groups : undefined,
        descriptorGroupOrder: order,
      };
    })(),
    spatialContext,
    contextSelectors,
    zonaCarrilEnabled: Boolean(spatialContext.zoneSelector || spatialContext.laneSelector || spatialContext.showFieldZone),
    zonas: buildLegacySelectorExport(source, contextSelectors, 'zone'),
    carriles: buildLegacySelectorExport(source, contextSelectors, 'lane'),
  };
}

export function buildPanelPayload(panel, overrides = {}) {
  const normalized = normalizePanelDefinition({
    ...panel,
    id: overrides.id || panel?.id,
    name: overrides.name || overrides.nombre || panel?.name || panel?.nombre,
  });

  if (!normalized) return null;

  const resolvedId = overrides.id || normalized.id;
  const resolvedName = overrides.name || overrides.nombre || normalized.name || normalized.nombre;
  const resolvedVersion = Number(panel?.version ?? normalized.version ?? 1) || 1;

  const payload = {
    id: resolvedId,
    name: resolvedName,
    version: resolvedVersion,
    description: normalized.description || '',
    sport: normalized.sport || '',
    source: normalized.source || '',
    groups: normalized.groups || [],
    descriptorButtons: normalized.descriptorButtons || [],
    descriptorGroups: Array.isArray(normalized.descriptorGroups) ? normalized.descriptorGroups : [],
    descriptorGroupOrder: normalized.descriptorGroupOrder || [],
    spatialContext: {
      showTeams: normalized.spatialContext.showTeams,
      showPlayers: normalized.spatialContext.showPlayers,
      showFieldZone: normalized.spatialContext.showFieldZone,
    },
  };

  if (overrides.userId) {
    payload.userId = overrides.userId;
  }

  return payload;
}

export function resolvePanelConfig(panelEntry) {
  return normalizePanelDefinition(panelEntry);
}

export function normalizePanelEntries(panels = []) {
  return (Array.isArray(panels) ? panels : [])
    .map((panel, index) => {
      const config = resolvePanelConfig(panel);
      if (!config) return null;

      const fallbackId = config.id
        || panel?.id
        || `panel_${index}_${slugify(config.name || config.nombre || panel?.nombre || panel?.name || 'panel', 'panel')}`;

      return {
        id: String(fallbackId),
        raw: panel,
        config: {
          ...config,
          id: config.id || String(fallbackId),
        },
      };
    })
    .filter(Boolean);
}

export function countPanelActions(panel) {
  return (panel?.groups || []).reduce((total, group) => total + (group?.actions?.length || 0), 0);
}
